import React, { useRef } from 'react'
import classnames from 'classnames'
import {
  AnalyticsEvent,
  renderIconSVG,
  Icon,
  triggerGoogleTagManagerCustomEvent,
  googleTagManagerCustomEvents,
} from '../../utils'
import { CTATheme } from '../../utils/constants/themes'
import { Text } from '../Text'
import { IProps } from './index'
import { LoadingAnimation } from '../LoadingAnimation'
import loadingSpinner from '../../images/loading.svg'

export const CTAButton: React.FC<IProps> = ({
  text,
  arrow = true,
  bgColor,
  small,
  onClick,
  dataAttribute,
  eventContext,
  type = 'button',
  loading,
  icon,
  disabled = false,
  children,
  refButton,
}) => {
  const dataAttributeObject = dataAttribute ? { [dataAttribute]: 'true' } : {}
  const analyticsEvent = new AnalyticsEvent({
    action: 'clicked',
    object: 'cta_button',
    context: eventContext || text.toLowerCase().split(' ').join('_'),
  })

  const handleClick = function (e: React.MouseEvent) {
    analyticsEvent.setPlaceFromPath(window.location.pathname)
    analyticsEvent.send()
    triggerGoogleTagManagerCustomEvent(googleTagManagerCustomEvents.ctaButtonClicked)
    return onClick(e)
  }

  const themeNames: Record<CTATheme, string> = {
    [CTATheme.BlackFill]: 'black-fill',
    [CTATheme.BlackOutline]: 'black-outline',
    [CTATheme.WhiteOutline]: 'white-outline',
    [CTATheme.LinkOnlyBlack]: 'link-only-black',
    [CTATheme.WhiteFill]: 'white-fill',
    [CTATheme.LinkOnlyWhite]: 'link-only-white',
    [CTATheme.TealFill]: 'teal-fill',
    [CTATheme.BlackFillTransparentHover]: 'black-fill-transparent-hover',
    [CTATheme.BlueFill]: 'blue-fill',
    [CTATheme.BlueOutline]: 'blue-outline',
    [CTATheme.LinkOnlyBlue]: 'link-only-blue',
  }

  const isValidChildren = React.isValidElement(children)

  const ref = useRef<HTMLButtonElement>(null)
  const buttonRef = refButton || ref

  return (
    <>
      <button
        type={type}
        className={classnames('cta-button', `cta-button--${themeNames[bgColor]}`, {
          'cta-button--small': small,
          'cta-button--unstyled': isValidChildren,
        })}
        data-testid="cta-button"
        {...dataAttributeObject}
        onClick={handleClick}
        disabled={loading || disabled}
        ref={buttonRef}
      >
        {isValidChildren ? (
          <>{children}</>
        ) : (
          <>
            {loading && (
              <div className="email-form__submit__loading-indicator">
                <LoadingAnimation />
              </div>
            )}
            <Text brand="default" size="button-bold">
              {text}
            </Text>
            {icon ? renderIconSVG(icon) : arrow && renderIconSVG(Icon.Arrow, 'cta-button__arrow')}
          </>
        )}
      </button>

      <style jsx>{`
        .cta-button {
          position: relative;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          transition: all 200ms;
          text-decoration: none;
          opacity: ${loading ? 0 : 1};
          cursor: pointer;

          &:not([class*='link-only']):not(.cta-button--unstyled) {
            height: 3.25rem;
            padding: 0 1.5rem;
            border-radius: 2rem;
            white-space: nowrap;
          }

          &[class*='link-only'] {
            &:focus,
            &:hover {
              text-decoration: underline;
            }
          }

          &:hover {
            text-decoration: none;

            :global(.cta-button__arrow) {
              animation: arrow 0.4s ease-in-out forwards;
            }
          }

          &:focus {
            outline: 0;
          }

          :global(span) {
            color: inherit;
          }

          &__icon {
            vertical-align: middle;
          }

          &.cta-button--unstyled {
            display: inline-block;
            border-radius: none;
            padding: 0;
            -webkit-text-decoration: none;
            text-decoration: none;
            text-align: unset;
            transition: unset;
            cursor: pointer;
            background-color: unset;
            border: none;
            color: unset;
          }

          &--small:not([class*='link-only']):not(.cta-link--unstyled) {
            height: 2.5rem;
          }

          &--wide {
            width: 100%;
          }

          &--loading {
            pointer-events: none;

            &::before {
              content: '';
              display: block;
              position: absolute;
              left: calc(50% - 12px);
              width: 24px;
              height: 24px;
              background-image: url(${loadingSpinner});
              background-repeat: no-repeat;
              background-position: center;
              background-size: 24px 24px;
              animation: spin 0.6s infinite linear;
            }

            @keyframes spin {
              0% {
                transform: rotate(0deg);
              }
              100% {
                transform: rotate(360deg);
              }
            }
          }

          &--black-fill,
          &--blue-fill,
          &--white-fill,
          &--black-outline,
          &--blue-outline,
          &--white-outline,
          &--black-fill-transparent-hover {
            &:active {
              transform: translateY(4px);
            }
          }

          &--black-fill {
            background-color: var(--slate);
            color: var(--white);
            border: 2px solid var(--slate);

            &:focus,
            &:hover {
              background-color: var(--white);
              color: var(--slate);
            }
          }

          &--black-outline {
            background-color: transparent;
            border: 2px solid var(--slate);
            color: var(--slate);

            &:focus,
            &:hover {
              background-color: var(--slate);
              color: var(--white);
            }
          }

          &--link-only-black {
            color: var(--slate);
            padding: 0;
            border-radius: 0;
          }

          &--black-fill-transparent-hover {
            background-color: var(--slate);
            box-shadow: inset 0 0 0 2px var(--slate);
            color: var(--white);

            &:focus,
            &:hover {
              background-color: transparent;
              box-shadow: inset 0 0 0 3px darken($slate, 15%);
              color: var(--slate);
            }
          }

          &--blue-fill {
            background-color: var(--blue);
            color: var(--white);
            border: 2px solid var(--blue);

            &:focus,
            &:hover {
              background-color: var(--white);
              color: var(--blue);
            }
          }

          &--blue-outline {
            background-color: transparent;
            border: 2px solid var(--blue);
            color: var(--blue);

            &:focus,
            &:hover {
              background-color: var(--blue);
              color: var(--white);
            }
          }

          &--link-only-blue {
            color: var(--blue);
            padding: 0;
            border-radius: 0;
          }

          &--teal-fill {
            background-color: var(--aqua);
            color: var(--slate);

            &:hover {
              background-color: var(--ice);
            }
          }

          &--white-fill {
            background-color: var(--white);
            color: var(--blue);

            &:focus,
            &:hover {
              background-color: var(--stone);
            }
          }

          &--white-outline {
            background-color: transparent;
            border: 2px solid var(--white);
            color: var(--white);

            &:focus,
            &:hover {
              background-color: transparent;
              border-color: var(--aqua);
              color: var(--aqua);
            }
          }

          &--link-only-white {
            color: var(--white);
            padding: 0;
            border-radius: 0;
          }

          :global(.cta-button__arrow) {
            transform: translate3d(0, 0, 0);
            animation: arrow-reverse 0.4s ease-in-out;
          }

          @keyframes arrow {
            0% {
              transform: translate3d(0, 0, 0);
            }
            40% {
              transform: translate3d(5px, 0, 0);
            }
            60% {
              transform: translate3d(3px, 0, 0);
            }
            100% {
              transform: translate3d(4px, 0, 0);
            }
          }

          @keyframes arrow-reverse {
            0% {
              transform: translate3d(4px, 0, 0);
            }
            40% {
              transform: translate3d(-1px, 0, 0);
            }
            60% {
              transform: translate3d(1px, 0, 0);
            }
            100% {
              transform: translate3d(0, 0, 0);
            }
          }
        }
      `}</style>
    </>
  )
}
